import React, { useEffect } from "react";
import { Clock, Users, Sparkles } from "lucide-react";

// Product images
const POG_RTD = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/t6qz6lo2_POG%20RTD%204.6.26%202.jpg";
const MADURO = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/mww36b3g_Maduro%2010.16.jpg";
const OJO_MEZCAL = "https://customer-assets.emergentagent.com/job_ty-core/artifacts/b8m9p7dv_Ojo.png";

const pogRecipes = [
  {
    id: 1,
    name: "POG Sunrise",
    description: "A refreshing tropical cocktail that captures the essence of a Hawaiian morning.",
    ingredients: ["2 oz POG Vodka", "4 oz Orange Juice", "1 oz Passion Fruit Syrup", "Splash of Grenadine", "Ice"],
    instructions: "Fill a glass with ice. Add POG Vodka and orange juice. Slowly pour passion fruit syrup and grenadine for a layered effect. Garnish with an orange slice.",
    difficulty: "Easy",
    servings: "1",
    time: "3 min",
    color: "pog",
  },
  {
    id: 2,
    name: "Guava Breeze",
    description: "A smooth, creamy cocktail with tropical guava notes and a hint of coconut.",
    ingredients: ["2 oz POG Vodka", "2 oz Coconut Cream", "2 oz Guava Nectar", "1 oz Lime Juice", "Ice"],
    instructions: "Combine all ingredients in a shaker with ice. Shake vigorously for 15 seconds. Strain into a chilled coupe glass. Garnish with a lime wheel.",
    difficulty: "Easy",
    servings: "1",
    time: "5 min",
    color: "pog",
  },
  {
    id: 3,
    name: "Tropical Paradise Punch",
    description: "Perfect for parties. A large-batch punch that brings the islands to your gathering.",
    ingredients: ["1 bottle POG Vodka", "2 cups Pineapple Juice", "1 cup Passion Fruit Juice", "1 cup Guava Nectar", "2 cups Sparkling Water", "Fresh Fruit"],
    instructions: "Combine all juices and vodka in a large punch bowl. Add ice and sparkling water just before serving. Float fresh tropical fruit slices on top.",
    difficulty: "Easy",
    servings: "12",
    time: "10 min",
    color: "pog",
  },
  {
    id: 4,
    name: "POG Martini",
    description: "An elegant twist on the classic martini with tropical flavors.",
    ingredients: ["3 oz POG Vodka", "1 oz Triple Sec", "1 oz Fresh Lime Juice", "0.5 oz Simple Syrup", "Ice"],
    instructions: "Add all ingredients to a cocktail shaker with ice. Shake until well-chilled. Double strain into a chilled martini glass. Garnish with a lime twist.",
    difficulty: "Medium",
    servings: "1",
    time: "5 min",
    color: "pog",
  },
];

const maduroRecipes = [
  {
    id: 5,
    name: "Maduro Old Fashioned",
    description: "A sophisticated twist on the classic, showcasing the deep, smoky notes of Maduro.",
    ingredients: ["2 oz Maduro Tequila", "0.5 oz Agave Syrup", "2 dashes Angostura Bitters", "Orange Peel", "Luxardo Cherry"],
    instructions: "Add agave syrup and bitters to a rocks glass. Add a large ice cube. Pour Maduro Tequila and stir gently. Express orange peel over the drink and garnish with cherry.",
    difficulty: "Easy",
    servings: "1",
    time: "3 min",
    color: "maduro",
  },
  {
    id: 6,
    name: "Smoky Cigar Lounge",
    description: "Designed to complement your favorite cigar with matching complexity.",
    ingredients: ["2 oz Maduro Tequila", "0.5 oz Mezcal", "0.5 oz Coffee Liqueur", "1 dash Chocolate Bitters", "Orange Zest"],
    instructions: "Stir all liquid ingredients with ice until well-chilled. Strain into a rocks glass over a large ice cube. Garnish with expressed orange zest.",
    difficulty: "Medium",
    servings: "1",
    time: "5 min",
    color: "maduro",
  },
];

const ojoRecipes = [
  {
    id: 7,
    name: "Smoky Margarita",
    description: "The classic margarita elevated with Ojo de Dios Joven's signature smoke.",
    ingredients: ["2 oz Ojo de Dios Joven", "1 oz Fresh Lime Juice", "0.75 oz Agave Syrup", "Tajín for rim", "Lime wheel"],
    instructions: "Rim a rocks glass with Tajín. Shake mezcal, lime juice, and agave with ice. Strain into the glass over fresh ice. Garnish with lime wheel.",
    difficulty: "Easy",
    servings: "1",
    time: "3 min",
    color: "ojo",
  },
  {
    id: 8,
    name: "Mezcal Espresso Martini",
    description: "Ojo de Dios Cafe shines in this smoky coffee cocktail perfection.",
    ingredients: ["2 oz Ojo de Dios Cafe", "1 oz Fresh Espresso", "0.5 oz Simple Syrup", "Coffee beans for garnish"],
    instructions: "Add all ingredients to a shaker with ice. Shake vigorously until frosty. Double strain into a chilled coupe glass. Garnish with 3 coffee beans.",
    difficulty: "Medium",
    servings: "1",
    time: "5 min",
    color: "ojo",
  },
  {
    id: 9,
    name: "Mexican Spritz",
    description: "Ojo de Dios Hibiscus creates a refreshing, vibrant summer cocktail.",
    ingredients: ["1.5 oz Ojo de Dios Hibiscus", "3 oz Prosecco", "1 oz Soda Water", "Fresh hibiscus flower or orange slice"],
    instructions: "Fill a wine glass with ice. Add Ojo de Dios Hibiscus. Top with Prosecco and soda water. Stir gently. Garnish with hibiscus flower or orange.",
    difficulty: "Easy",
    servings: "1",
    time: "2 min",
    color: "ojo",
  },
  {
    id: 10,
    name: "Oaxacan Coffee Negroni",
    description: "A bold twist on the Negroni using Ojo de Dios Cafe for depth.",
    ingredients: ["1 oz Ojo de Dios Cafe", "1 oz Sweet Vermouth", "1 oz Campari", "Orange Peel"],
    instructions: "Add all liquid ingredients to a mixing glass with ice. Stir for 30 seconds until well-chilled. Strain into a rocks glass over a large ice cube. Express orange peel and drop in.",
    difficulty: "Medium",
    servings: "1",
    time: "4 min",
    color: "ojo",
  },
];

const Recipes = () => {
  // SEO - Dynamic page title
  useEffect(() => {
    document.title = "Vodka & Tequila Cocktail Recipes | POG Vodka Drinks & Maduro Tequila Cocktails";
  }, []);

  return (
    <div className="bg-[#050505] pt-20" data-testid="recipes-page">
      {/* Hero - SEO Optimized */}
      <section className="py-24 sm:py-32" aria-label="Cocktail Recipes">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto">
            <p className="font-subheading text-xs tracking-[0.3em] uppercase text-[#C8A876] mb-4">
              Cocktail Recipes & Mixology
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl tracking-tighter text-white mb-6">
              Flavored Vodka & Tequila Cocktails
            </h1>
            <p className="text-[#A3A3A3] text-lg">
              Discover the best tropical vodka cocktails with POG Vodka, sophisticated tequila drinks with Maduro, 
              and smoky mezcal cocktails with Ojo de Dios. Easy recipes for home bartenders.
            </p>
          </div>
        </div>
      </section>

      {/* POG Vodka Recipes */}
      <section className="pb-24" data-testid="pog-recipes-section">
        <div className="container-main">
          <div className="flex items-center gap-6 mb-12">
            <img src={POG_RTD} alt="POG Vodka" className="w-20 h-auto object-contain" />
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">
                <span className="gradient-text-pog">POG Vodka</span>
              </h2>
              <p className="text-[#A3A3A3]">Tropical cocktails & refreshers</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {pogRecipes.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        </div>
      </section>

      {/* Maduro Tequila Recipes */}
      <section className="py-24 bg-[#0A0A0A]" data-testid="maduro-recipes-section">
        <div className="container-main">
          <div className="flex items-center gap-6 mb-12">
            <img src={MADURO} alt="Maduro Tequila" className="w-20 h-auto object-contain" />
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">
                <span className="gradient-text-maduro">Maduro Tequila</span>
              </h2>
              <p className="text-[#A3A3A3]">Sophisticated serves & sipping rituals</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {maduroRecipes.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        </div>
      </section>

      {/* Ojo de Dios Mezcal Recipes */}
      <section className="py-24" data-testid="ojo-recipes-section">
        <div className="container-main">
          <div className="flex items-center gap-6 mb-12">
            <img src={OJO_MEZCAL} alt="Ojo de Dios Mezcal" className="w-20 h-auto object-contain" />
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">
                <span className="bg-gradient-to-r from-[#00CED1] to-[#FFD700] bg-clip-text text-transparent">Ojo de Dios</span>
              </h2>
              <p className="text-[#A3A3A3]">Modern mezcal cocktails & serves</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {ojoRecipes.map((recipe) => (
              <RecipeCard key={recipe.id} recipe={recipe} />
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-24" data-testid="tips-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-white mb-4">
              Pro Tips
            </h2>
            <p className="text-[#A3A3A3]">Elevate your cocktail game</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Ice Matters",
                description: "Use large ice cubes for slow dilution in spirit-forward drinks. Crushed ice for tropical refreshers.",
              },
              {
                title: "Fresh is Best",
                description: "Always use fresh citrus juice. Pre-squeezed juice loses brightness within hours.",
              },
              {
                title: "Chill Your Glass",
                description: "A cold glass keeps your cocktail at the perfect temperature longer. 5 minutes in the freezer works.",
              },
            ].map((tip) => (
              <div key={tip.title} className="text-center p-8 border border-white/5">
                <div className="w-12 h-12 mx-auto mb-4 border border-[#C8A876] flex items-center justify-center">
                  <Sparkles size={20} className="text-[#C8A876]" />
                </div>
                <h3 className="text-white text-lg font-medium mb-2">{tip.title}</h3>
                <p className="text-[#737373] text-sm">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

const RecipeCard = ({ recipe }) => {
  const isPog = recipe.color === "pog";
  const isOjo = recipe.color === "ojo";
  
  const getAccentColor = () => {
    if (isPog) return "#FF6B35";
    if (isOjo) return "#00CED1";
    return "#C8A876";
  };
  
  const getBgClass = () => {
    if (isPog) return "bg-[#1A1025] border-[#3B1F54]";
    if (isOjo) return "bg-[#0A1A1A] border-[#00CED1]/30";
    return "bg-[#1A1A1A] border-[#333333]";
  };
  
  const accentColor = getAccentColor();
  const bgClass = getBgClass();

  return (
    <div 
      className={`p-8 border ${bgClass} card-dark`}
      data-testid={`recipe-card-${recipe.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-heading text-2xl text-white mb-1">{recipe.name}</h3>
          <p className="text-[#A3A3A3] text-sm">{recipe.description}</p>
        </div>
        <span 
          className="text-xs px-3 py-1 border"
          style={{ borderColor: accentColor, color: accentColor }}
        >
          {recipe.difficulty}
        </span>
      </div>

      <div className="flex gap-6 mb-6 text-sm text-[#737373]">
        <span className="flex items-center gap-2">
          <Clock size={14} style={{ color: accentColor }} />
          {recipe.time}
        </span>
        <span className="flex items-center gap-2">
          <Users size={14} style={{ color: accentColor }} />
          Serves {recipe.servings}
        </span>
      </div>

      <div className="mb-6">
        <h4 className="text-white text-sm font-medium mb-3">Ingredients</h4>
        <ul className="space-y-1">
          {recipe.ingredients.map((ing) => (
            <li key={ing} className="text-[#A3A3A3] text-sm flex items-start gap-2">
              <span style={{ color: accentColor }}>•</span>
              {ing}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="text-white text-sm font-medium mb-3">Instructions</h4>
        <p className="text-[#A3A3A3] text-sm leading-relaxed">{recipe.instructions}</p>
      </div>
    </div>
  );
};

export default Recipes;
